/**
 * 関西 AI Hub — 共通型定義・列挙値・定数
 * フロントエンド・バックエンドで共有される ドメイン値（Prisma スキーマと整合）
 */
// =============================================================================
// User
// =============================================================================
export const USER_ROLES = ["ENTERPRISE", "MEMBER", "ADMIN"];
export const USER_ROLE_LABELS = {
    ENTERPRISE: "企業",
    MEMBER: "大学・JAM BASE 会員企業",
    ADMIN: "事務局",
};
// =============================================================================
// UseCase
// =============================================================================
export const CASE_STATUSES = ["DRAFT", "OPEN", "IN_PROGRESS", "COMPLETED", "SUSPENDED"];
export const CASE_STATUS_LABELS = {
    DRAFT: "下書き",
    OPEN: "募集中",
    IN_PROGRESS: "進行中",
    COMPLETED: "完了",
    SUSPENDED: "停止中",
};
/**
 * Mantine の color プロパティに渡せる色キー（theme.colors のキーまたは標準色）
 */
// 主色（brand-primary）が赤なので、停止・否定の状態に赤を使うと通常のアクセントと見分けられない。
// 停止中・不採用は status-negative（黒に近い濃いグレー。web の Mantine テーマに登録した色）にする
// （spec/frontend/design-system.md「主色」）。
export const CASE_STATUS_MANTINE_COLOR = {
    DRAFT: "gray",
    OPEN: "cta-blue",
    IN_PROGRESS: "green",
    COMPLETED: "gray",
    SUSPENDED: "status-negative",
};
// =============================================================================
// Application
// =============================================================================
export const APPLICATION_STATUSES = [
    "PENDING_AUDIT",
    "NEW",
    "UNDER_REVIEW",
    "APPROVED",
    "REJECTED",
    "AUDIT_BLOCKED",
];
/**
 * 「企業に露出するステータス」のサブセット。
 * ENTERPRISE 向け一覧のデフォルト where に使う（AUDIT 系を自動除外）。
 */
export const ENTERPRISE_VISIBLE_APPLICATION_STATUSES = [
    "NEW",
    "UNDER_REVIEW",
    "APPROVED",
    "REJECTED",
];
export const APPLICATION_STATUS_LABELS = {
    PENDING_AUDIT: "監査中",
    NEW: "新着",
    UNDER_REVIEW: "検討中",
    APPROVED: "承認",
    REJECTED: "不採用",
    AUDIT_BLOCKED: "監査により非公開",
};
/**
 * 応募者（MEMBER）視点のラベル。"NEW" は企業側には「新着応募が来た」の意味だが、
 * 応募者本人からは自分が「応募済み」であることを示すため、この状態だけ表現を変える。
 * それ以外のステータスは共通ラベルをそのまま使う（"PENDING_AUDIT" も「監査中」のまま。
 * マイ応募のステータスフィルタからは除外するので、フィルタ上で AUDIT_BLOCKED の
 * 「監査により非公開」と混同することはない。詳細は my-applications-page.tsx 参照）。
 */
export const APPLICATION_STATUS_LABELS_APPLICANT = {
    ...APPLICATION_STATUS_LABELS,
    NEW: "応募済み",
};
export const APPLICATION_STATUS_MANTINE_COLOR = {
    PENDING_AUDIT: "gray",
    NEW: "cta-blue",
    UNDER_REVIEW: "orange",
    APPROVED: "green",
    REJECTED: "status-negative",
    // AI 監査による拒否は、人間判断の不採用（status-negative）と別系統の警告色（琥珀）。web の Mantine テーマに登録した色
    AUDIT_BLOCKED: "status-blocked",
};
// =============================================================================
// AI 監査
// =============================================================================
export const AUDIT_VERDICTS = ["PASS", "FLAGGED", "BLOCKED"];
export const AUDIT_VERDICT_LABELS = {
    PASS: "監査クリア",
    FLAGGED: "要レビュー",
    BLOCKED: "不適合",
};
// 不適合（BLOCKED）は応募ステータスの AUDIT_BLOCKED と同じ琥珀にする（同じ画面に並ぶため）。
export const AUDIT_VERDICT_MANTINE_COLOR = {
    PASS: "green",
    FLAGGED: "orange",
    BLOCKED: "status-blocked",
};
export const AUDIT_DIMENSIONS = [
    "REQUIREMENT_FIT",
    "PROPOSAL_VALIDITY",
    "SPAM_SALES",
    "CASE_RELEVANCE",
    "PROPOSAL_SPECIFICITY",
    "POLICY_VIOLATION",
    "DUPLICATE_SIMILARITY",
    "CONTACT_INTEGRITY",
    "BLACKLIST",
];
/**
 * 6 つは LLM 評価、3 つは決定論評価。
 * @see spec/ai/posting-audit/02-criteria.md
 */
export const LLM_AUDIT_DIMENSIONS = [
    "REQUIREMENT_FIT",
    "PROPOSAL_VALIDITY",
    "SPAM_SALES",
    "CASE_RELEVANCE",
    "PROPOSAL_SPECIFICITY",
    "POLICY_VIOLATION",
];
export const AUDIT_DIMENSION_LABELS = {
    REQUIREMENT_FIT: "要件適合度",
    PROPOSAL_VALIDITY: "提案の妥当性",
    SPAM_SALES: "営業・スパム判定",
    CASE_RELEVANCE: "案件との関連性",
    PROPOSAL_SPECIFICITY: "提案の具体性",
    POLICY_VIOLATION: "ポリシー違反",
    DUPLICATE_SIMILARITY: "過去応募類似度",
    CONTACT_INTEGRITY: "連絡先整合性",
    BLACKLIST: "ブラックリスト",
};
// =============================================================================
// Notification（アプリ内通知）
// =============================================================================
export const NOTIFICATION_TYPES = [
    "CASE_SUSPENDED",
    "APPLICATION_RECEIVED",
    "APPLICATION_UNDER_REVIEW",
    "APPLICATION_APPROVED",
    "APPLICATION_REJECTED",
    "APPLICATION_MATCHED",
];
/** ベル一覧の本文テンプレ用ラベル（種別の短い説明）。文面組み立ては UI 側で行う。 */
export const NOTIFICATION_TYPE_LABELS = {
    CASE_SUSPENDED: "案件が事務局により停止されました",
    APPLICATION_RECEIVED: "新しい応募が届きました",
    APPLICATION_UNDER_REVIEW: "応募が検討中になりました",
    APPLICATION_APPROVED: "応募が承認されました",
    APPLICATION_REJECTED: "応募が不採用になりました",
    APPLICATION_MATCHED: "マッチングが成立しました",
};
/**
 * 通知の見え方（Mantine color）。種別ごとの強調色。
 * 停止・不採用は案件・応募のステータスと同じ status-negative にする（主色の赤と紛れないように）。
 * 通知ベルは `${color}.6` で参照するので、ここには shade を付けない色名だけを書く。
 */
export const NOTIFICATION_TYPE_MANTINE_COLOR = {
    CASE_SUSPENDED: "status-negative",
    APPLICATION_RECEIVED: "cta-blue",
    APPLICATION_UNDER_REVIEW: "orange",
    APPLICATION_APPROVED: "green",
    APPLICATION_REJECTED: "status-negative",
    APPLICATION_MATCHED: "green",
};
/**
 * アカウント 1 件あたりに登録できる「通知メール送信先」の上限。
 * アプリ内通知が発火したとき、その受信者(User)の登録メール全件へ ACS Email で配信する。
 * ACS のコストは 1 通 ≈ $0.0003 で上限要因にならないため、UX / 誤登録防止の観点で添付上限と揃えて 5 件とする。
 */
export const MAX_NOTIFICATION_EMAILS = 5;
/**
 * 「お問い合わせが届いたときに通知するメールアドレス」の上限（事務局のメーリングリスト）。
 *
 * `MAX_NOTIFICATION_EMAILS` とは**別物**。あちらは User 1 件ごとの通知先で、こちらは
 * 事務局全体で 1 つ持つ受信通知の宛先。名前が似ているので取り違えないこと。
 */
export const MAX_INQUIRY_NOTIFICATION_EMAILS = 10;
/**
 * 「アカウント発行のお知らせ」メールの履歴（`MAIL_SENT` / `MAIL_FAILED`）に付く
 * コメントの共通接頭辞。
 *
 * `InquiryEvent` には**どのメールの話かを表す列が無い**。1 件の問い合わせには
 * 受信通知・受付の自動返信・アカウント発行のお知らせと 3 種類のメールが絡むので、
 * 型だけで見ると「最後のメール履歴が失敗」＝「発行のお知らせが届いていない」とは
 * 限らない。詳細画面の再送ボタンは**この接頭辞で絞ってから**判定する。
 *
 * 書く側（`inquiry-mailer.service.ts`）と読む側（`inquiry-detail-page.tsx`）で
 * 文字列が割れると、警告と再送ボタンが黙って出なくなる。だから共有の定数にする。
 */
export const ACCOUNT_MAIL_EVENT_PREFIX = "アカウント発行のお知らせ";
// =============================================================================
// Budget
// =============================================================================
export const BUDGET_RANGES = [
    "UNDER_1M",
    "UNDER_3M",
    "UNDER_5M",
    "UNDER_10M",
    "OVER_10M",
    "UNDECIDED",
];
export const BUDGET_RANGE_LABELS = {
    UNDER_1M: "〜100万円",
    UNDER_3M: "〜300万円",
    UNDER_5M: "〜500万円",
    UNDER_10M: "〜1,000万円",
    OVER_10M: "1,000万円以上",
    UNDECIDED: "未定",
};
// =============================================================================
// Target Audience
// =============================================================================
export const TARGET_AUDIENCES = ["UNIVERSITY", "COMPANY", "BOTH"];
export const TARGET_AUDIENCE_LABELS = {
    UNIVERSITY: "大学",
    COMPANY: "企業",
    BOTH: "大学・企業",
};
// =============================================================================
// Industry / Technology タグ
// =============================================================================
export const INDUSTRY_OPTIONS = [
    "製造",
    "物流",
    "小売",
    "金融",
    "医療・ヘルスケア",
    "建設・不動産",
    "エネルギー",
    "IT・通信",
    "農業・食品",
    "その他",
];
export const TECHNOLOGY_OPTIONS = [
    "AI/ML",
    "画像認識",
    "自然言語処理",
    "IoT",
    "ロボティクス",
    "データ分析",
    "最適化",
    "ブロックチェーン",
    "AR/VR",
    "クラウド",
    "セキュリティ",
    "その他",
];
// =============================================================================
// Pagination
// =============================================================================
export const DEFAULT_PAGE_SIZE = 12;
export const MAX_PAGE_SIZE = 100;
